import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Trophy, Medal, Users, Calendar, Plus, Edit, Trash2, Loader2, 
  Play, CheckCircle, Flag, Crown, Swords, Shield, Settings,
  ArrowRight, Save, Eye, Printer, Download, RefreshCw, Zap, Target,
  ChevronRight, ChevronUp, ChevronDown, Award, Star, Goal, User, Search, Mail, FileText,
  Edit3, Sparkles
} from 'lucide-react';
import api from '../lib/api';

// Modalidades disponíveis
const MODALIDADES = [
  { id: 'futebol', nome: 'Futebol', icon: '⚽', cor: 'bg-green-500' },
  { id: 'futsal', nome: 'Futsal', icon: '⚽', cor: 'bg-green-600' },
  { id: 'volei', nome: 'Vôlei', icon: '🏐', cor: 'bg-yellow-500' },
  { id: 'basquete', nome: 'Basquete', icon: '🏀', cor: 'bg-orange-500' },
  { id: 'handebol', nome: 'Handebol', icon: '🤾', cor: 'bg-blue-500' },
];

// Gêneros
const GENEROS = [
  { id: 'masculino', nome: 'Masculino', icon: '♂️' },
  { id: 'feminino', nome: 'Feminino', icon: '♀️' },
  { id: 'misto', nome: 'Misto', icon: '⚥' },
];

const TorneioProfissional = () => {
  // Estados principais
  const [torneios, setTorneios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [torneioSelecionado, setTorneioSelecionado] = useState(null);
  const [activeView, setActiveView] = useState('admin');
  const [salvando, setSalvando] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState(null);
  
  // Dados auxiliares
  const [turmas, setTurmas] = useState([]);
  const [alunosPorTurma, setAlunosPorTurma] = useState({});
  
  // Estados do formulário de criação
  const [showCriarModal, setShowCriarModal] = useState(false);
  const [formTorneio, setFormTorneio] = useState({
    nome: '',
    modalidade: 'futebol',
    genero: 'masculino',
    num_grupos: 2,
    times_por_grupo: 4,
    classificados_por_grupo: 4,
    descricao: '',
  });
  
  // Estados de gerenciamento de times
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [timeEditando, setTimeEditando] = useState(null);
  const [formTime, setFormTime] = useState({ nome: '', turma_id: '', jogadores: [] });
  const [alunosDisponiveis, setAlunosDisponiveis] = useState([]);
  const [buscaAluno, setBuscaAluno] = useState('');
  const [modoSelecaoAlunos, setModoSelecaoAlunos] = useState('turma'); // 'turma' ou 'todos'
  
  // Estados de partidas e gols
  const [showPartidaModal, setShowPartidaModal] = useState(false);
  const [partidaEditando, setPartidaEditando] = useState(null);
  const [placar, setPlacar] = useState({ placar1: 0, placar2: 0, gols_time1: [], gols_time2: [] });
  const [golTemp1, setGolTemp1] = useState({ jogador_id: '', jogador_nome: '', quantidade: 1 });
  const [golTemp2, setGolTemp2] = useState({ jogador_id: '', jogador_nome: '', quantidade: 1 });
  
  // Estados de impressão
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printType, setPrintType] = useState('grupos');
  const printRef = useRef(null);
  const [baixandoPDF, setBaixandoPDF] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailDestinatarios, setEmailDestinatarios] = useState('');
  const [emailAssunto, setEmailAssunto] = useState('');
  const [emailMensagem, setEmailMensagem] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  
  // Estados para edição de torneio e IA
  const [showEditarNomeModal, setShowEditarNomeModal] = useState(false);
  const [novoNomeTorneio, setNovoNomeTorneio] = useState('');
  const [showCronogramaIAModal, setShowCronogramaIAModal] = useState(false);
  const [cronogramaIA, setCronogramaIA] = useState({
    data_inicio: new Date().toISOString().split('T')[0],
    horario_inicio: '08:00',
    intervalo_minutos: 30,
    jogos_por_dia: 10
  });
  const [gerandoCronograma, setGerandoCronograma] = useState(false);
  
  // Estados para configuração manual de grupos
  const [showGruposManualModal, setShowGruposManualModal] = useState(false);
  const [gruposManual, setGruposManual] = useState([]);

  // Carregar dados iniciais
  useEffect(() => {
    loadTorneios();
    loadTurmas();
    loadSchoolLogo();
  }, []);

  const loadSchoolLogo = async () => {
    try {
      const res = await api.get('/settings/school-logo');
      if (res.data?.logo_url) {
        setSchoolLogo(res.data.logo_url);
      }
    } catch (error) {
      console.log('Logo não encontrado');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post('/settings/school-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSchoolLogo(res.data.logo_url);
      toast.success('Logo atualizado!');
    } catch (error) {
      toast.error('Erro ao fazer upload do logo');
    }
  };

  const loadTorneios = async () => {
    setLoading(true);
    try {
      const res = await api.get('/torneios-profissionais');
      setTorneios(res.data || []);
    } catch (error) {
      setTorneios([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTurmas = async () => {
    try {
      const res = await api.get('/class-groups');
      setTurmas(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };

  const loadAlunosTurma = async (turmaId) => {
    // Limpar cache para garantir dados atualizados da turma correta
    try {
      const res = await api.get(`/students?class_id=${turmaId}`);
      const alunos = res.data || [];
      setAlunosPorTurma(prev => ({ ...prev, [turmaId]: alunos }));
      setAlunosDisponiveis(alunos);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      setAlunosDisponiveis([]);
    }
  };

  // Carregar TODOS os alunos da escola (para permitir jogadores de outras turmas)
  const loadTodosAlunos = async () => {
    try {
      const res = await api.get('/students');
      const alunos = (res.data || []).filter(a => a.situation === 'Ativo');
      setAlunosDisponiveis(alunos);
    } catch (error) {
      console.error('Erro ao carregar todos os alunos:', error);
      setAlunosDisponiveis([]);
    }
  };

  // ============================================
  // FUNÇÕES DE CÁLCULO
  // ============================================

  const calcularClassificacaoGrupo = (torneio, grupoLetra) => {
    if (!torneio?.grupos || !torneio?.partidas_grupos) return [];
    
    const grupo = torneio.grupos.find(g => g.letra === grupoLetra);
    if (!grupo) return [];
    
    const tabela = {};
    
    grupo.times.forEach(time => {
      tabela[time.id] = {
        id: time.id,
        nome: time.nome,
        pontos: 0,
        jogos: 0,
        vitorias: 0,
        empates: 0,
        derrotas: 0,
        gols_pro: 0,
        gols_contra: 0,
        saldo: 0,
        confrontos: {}
      };
    });
    
    const partidasGrupo = torneio.partidas_grupos.filter(p => p.grupo === grupoLetra && p.status === 'finalizada');
    
    partidasGrupo.forEach(partida => {
      const t1 = tabela[partida.time1_id];
      const t2 = tabela[partida.time2_id];
      if (!t1 || !t2) return;
      
      const p1 = parseInt(partida.placar1) || 0;
      const p2 = parseInt(partida.placar2) || 0;
      
      t1.jogos++;
      t2.jogos++;
      t1.gols_pro += p1;
      t1.gols_contra += p2;
      t2.gols_pro += p2;
      t2.gols_contra += p1;
      
      if (!t1.confrontos[partida.time2_id]) t1.confrontos[partida.time2_id] = { gp: 0, gc: 0 };
      if (!t2.confrontos[partida.time1_id]) t2.confrontos[partida.time1_id] = { gp: 0, gc: 0 };
      t1.confrontos[partida.time2_id].gp += p1;
      t1.confrontos[partida.time2_id].gc += p2;
      t2.confrontos[partida.time1_id].gp += p2;
      t2.confrontos[partida.time1_id].gc += p1;
      
      if (p1 > p2) {
        t1.vitorias++;
        t1.pontos += 3;
        t2.derrotas++;
      } else if (p2 > p1) {
        t2.vitorias++;
        t2.pontos += 3;
        t1.derrotas++;
      } else {
        t1.empates++;
        t2.empates++;
        t1.pontos++;
        t2.pontos++;
      }
      
      t1.saldo = t1.gols_pro - t1.gols_contra;
      t2.saldo = t2.gols_pro - t2.gols_contra;
    });
    
    return Object.values(tabela).sort((a, b) => {
      if (b.pontos !== a.pontos) return b.pontos - a.pontos;
      if (b.vitorias !== a.vitorias) return b.vitorias - a.vitorias;
      if (b.saldo !== a.saldo) return b.saldo - a.saldo;
      if (b.gols_pro !== a.gols_pro) return b.gols_pro - a.gols_pro;
      const confA = a.confrontos[b.id];
      const confB = b.confrontos[a.id];
      if (confA && confB) {
        const saldoA = confA.gp - confA.gc;
        const saldoB = confB.gp - confB.gc;
        if (saldoB !== saldoA) return saldoB - saldoA;
      }
      return Math.random() - 0.5;
    });
  };

  const calcularArtilheiros = (torneio) => {
    if (!torneio) return [];
    
    const artilheiros = {};
    
    // Gols da fase de grupos
    (torneio.partidas_grupos || []).forEach(partida => {
      (partida.gols_time1 || []).forEach(gol => {
        const key = gol.jogador_id || gol.jogador_nome;
        if (!artilheiros[key]) {
          artilheiros[key] = {
            jogador_id: gol.jogador_id,
            nome: gol.jogador_nome,
            time_id: partida.time1_id,
            time_nome: partida.time1_nome,
            gols: 0
          };
        }
        artilheiros[key].gols += parseInt(gol.quantidade) || 1;
      });
      (partida.gols_time2 || []).forEach(gol => {
        const key = gol.jogador_id || gol.jogador_nome;
        if (!artilheiros[key]) {
          artilheiros[key] = {
            jogador_id: gol.jogador_id,
            nome: gol.jogador_nome,
            time_id: partida.time2_id,
            time_nome: partida.time2_nome,
            gols: 0
          };
        }
        artilheiros[key].gols += parseInt(gol.quantidade) || 1;
      });
    });
    
    // Gols do mata-mata
    (torneio.partidas_matamata || []).forEach(partida => {
      (partida.gols_time1 || []).forEach(gol => {
        const key = gol.jogador_id || gol.jogador_nome;
        if (!artilheiros[key]) {
          artilheiros[key] = {
            jogador_id: gol.jogador_id,
            nome: gol.jogador_nome,
            time_id: partida.time1_id,
            time_nome: partida.time1_nome,
            gols: 0
          };
        }
        artilheiros[key].gols += parseInt(gol.quantidade) || 1;
      });
      (partida.gols_time2 || []).forEach(gol => {
        const key = gol.jogador_id || gol.jogador_nome;
        if (!artilheiros[key]) {
          artilheiros[key] = {
            jogador_id: gol.jogador_id,
            nome: gol.jogador_nome,
            time_id: partida.time2_id,
            time_nome: partida.time2_nome,
            gols: 0
          };
        }
        artilheiros[key].gols += parseInt(gol.quantidade) || 1;
      });
    });
    
    return Object.values(artilheiros)
      .filter(a => a.gols > 0)
      .sort((a, b) => b.gols - a.gols);
  };

  const gerarPartidasGrupo = (times, grupoLetra) => {
    const partidas = [];
    for (let i = 0; i < times.length; i++) {
      for (let j = i + 1; j < times.length; j++) {
        partidas.push({
          id: `${grupoLetra}-${times[i].id}-${times[j].id}`,
          grupo: grupoLetra,
          time1_id: times[i].id,
          time2_id: times[j].id,
          time1_nome: times[i].nome,
          time2_nome: times[j].nome,
          placar1: 0,
          placar2: 0,
          gols_time1: [],
          gols_time2: [],
          data: '',
          horario: '',
          status: 'agendada',
          fase: 'grupos'
        });
      }
    }
    return partidas;
  };

  const embaralharPartidas = (partidas) => {
    // Embaralhar para que times não joguem em sequência
    const embaralhadas = [...partidas];
    for (let i = embaralhadas.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [embaralhadas[i], embaralhadas[j]] = [embaralhadas[j], embaralhadas[i]];
    }
    
    // Verificar se algum time joga duas vezes seguidas e reordenar se necessário
    let tentativas = 0;
    let temConflito = true;
    
    while (temConflito && tentativas < 100) {
      temConflito = false;
      for (let i = 0; i < embaralhadas.length - 1; i++) {
        const atual = embaralhadas[i];
        const proxima = embaralhadas[i + 1];
        
        if (atual.time1_id === proxima.time1_id || atual.time1_id === proxima.time2_id ||
            atual.time2_id === proxima.time1_id || atual.time2_id === proxima.time2_id) {
          // Tentar trocar com uma partida mais distante
          for (let j = i + 2; j < embaralhadas.length; j++) {
            const candidata = embaralhadas[j];
            if (atual.time1_id !== candidata.time1_id && atual.time1_id !== candidata.time2_id &&
                atual.time2_id !== candidata.time1_id && atual.time2_id !== candidata.time2_id) {
              [embaralhadas[i + 1], embaralhadas[j]] = [embaralhadas[j], embaralhadas[i + 1]];
              break;
            }
          }
          temConflito = true;
        }
      }
      tentativas++;
    }
    
    return embaralhadas;
  };

  // ============================================
  // HANDLERS
  // ============================================

  const handleCriarTorneio = async () => {
    if (!formTorneio.nome) {
      toast.error('Digite o nome do torneio');
      return;
    }
    
    setSalvando(true);
    try {
      const novoTorneio = {
        id: `torneio-${Date.now()}`,
        ...formTorneio,
        times: [],
        grupos: [],
        partidas_grupos: [],
        partidas_matamata: [],
        status: 'configurando',
        created_at: new Date().toISOString(),
      };
      
      await api.post('/torneios-profissionais', novoTorneio);
      toast.success('Torneio criado!');
      setShowCriarModal(false);
      setFormTorneio({ nome: '', modalidade: 'futebol', genero: 'masculino', num_grupos: 2, times_por_grupo: 4, classificados_por_grupo: 4, descricao: '' });
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao criar torneio');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluirTorneio = async (torneioId) => {
    if (!window.confirm('Tem certeza que deseja EXCLUIR este torneio? Esta ação não pode ser desfeita.')) return;
    
    try {
      await api.delete(`/torneios-profissionais/${torneioId}`);
      toast.success('Torneio excluído!');
      setTorneioSelecionado(null);
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao excluir torneio');
    }
  };

  const abrirModalTime = (time = null) => {
    if (time) {
      setTimeEditando(time);
      setFormTime({ nome: time.nome, turma_id: time.turma_id || '', jogadores: time.jogadores || [] });
      if (time.turma_id) {
        setModoSelecaoAlunos('turma');
        loadAlunosTurma(time.turma_id);
      } else {
        setModoSelecaoAlunos('todos');
        loadTodosAlunos();
      }
    } else {
      setTimeEditando(null);
      setFormTime({ nome: '', turma_id: '', jogadores: [] });
      setAlunosDisponiveis([]);
      setModoSelecaoAlunos('turma');
    }
    setBuscaAluno('');
    setShowTimeModal(true);
  };

  const handleSalvarTime = async () => {
    if (!formTime.nome.trim() || !torneioSelecionado) {
      toast.error('Digite o nome do time');
      return;
    }
    
    try {
      let timesAtualizados;
      
      if (timeEditando) {
        timesAtualizados = torneioSelecionado.times.map(t => 
          t.id === timeEditando.id ? { ...t, ...formTime } : t
        );
      } else {
        const novoTime = {
          id: `time-${Date.now()}`,
          ...formTime
        };
        timesAtualizados = [...(torneioSelecionado.times || []), novoTime];
      }
      
      await api.put(`/torneios-profissionais/${torneioSelecionado.id}`, {
        ...torneioSelecionado,
        times: timesAtualizados
      });
      
      setTorneioSelecionado(prev => ({ ...prev, times: timesAtualizados }));
      setShowTimeModal(false);
      toast.success(timeEditando ? 'Time atualizado!' : 'Time adicionado!');
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao salvar time');
    }
  };

  const handleRemoverTime = async (timeId) => {
    if (!window.confirm('Remover este time?')) return;
    
    try {
      const timesAtualizados = torneioSelecionado.times.filter(t => t.id !== timeId);
      await api.put(`/torneios-profissionais/${torneioSelecionado.id}`, {
        ...torneioSelecionado,
        times: timesAtualizados
      });
      
      setTorneioSelecionado(prev => ({ ...prev, times: timesAtualizados }));
      toast.success('Time removido!');
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao remover time');
    }
  };

  const toggleJogadorTime = (aluno) => {
    const jaNoTime = formTime.jogadores.some(j => j.id === aluno.id);
    if (jaNoTime) {
      setFormTime(prev => ({
        ...prev,
        jogadores: prev.jogadores.filter(j => j.id !== aluno.id)
      }));
    } else {
      setFormTime(prev => ({
        ...prev,
        jogadores: [...prev.jogadores, { id: aluno.id, nome: aluno.name }]
      }));
    }
  };

  const handleDistribuirGrupos = async () => {
    if (!torneioSelecionado) return;
    
    const { times, num_grupos, times_por_grupo } = torneioSelecionado;
    const totalNecessario = num_grupos * times_por_grupo;
    
    if (times.length < totalNecessario) {
      toast.error(`Você precisa de ${totalNecessario} times. Atualmente tem ${times.length}.`);
      return;
    }
    
    const timesEmbaralhados = [...times].sort(() => Math.random() - 0.5);
    
    const grupos = [];
    const letras = 'ABCDEFGH';
    for (let i = 0; i < num_grupos; i++) {
      const inicio = i * times_por_grupo;
      const timesGrupo = timesEmbaralhados.slice(inicio, inicio + times_por_grupo);
      grupos.push({
        letra: letras[i],
        times: timesGrupo
      });
    }
    
    let todasPartidas = [];
    grupos.forEach(grupo => {
      const partidasGrupo = gerarPartidasGrupo(grupo.times, grupo.letra);
      todasPartidas = [...todasPartidas, ...partidasGrupo];
    });
    
    // Embaralhar partidas para não ter times jogando em sequência
    todasPartidas = embaralharPartidas(todasPartidas);
    
    try {
      await api.put(`/torneios-profissionais/${torneioSelecionado.id}`, {
        ...torneioSelecionado,
        grupos,
        partidas_grupos: todasPartidas,
        status: 'grupos'
      });
      
      setTorneioSelecionado(prev => ({
        ...prev,
        grupos,
        partidas_grupos: todasPartidas,
        status: 'grupos'
      }));
      
      toast.success('Grupos sorteados e partidas geradas!');
      setActiveView('grupos');
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao distribuir grupos');
    }
  };

  // Abrir modal para configuração manual de grupos
  const abrirConfigGruposManual = () => {
    const { times, num_grupos } = torneioSelecionado;
    const letras = 'ABCDEFGH';
    
    // Se já tem grupos, usar como base
    if (torneioSelecionado.grupos?.length > 0) {
      setGruposManual(torneioSelecionado.grupos.map(g => ({
        letra: g.letra,
        times: g.times.map(t => t.id)
      })));
    } else {
      // Criar grupos vazios
      const novosGrupos = [];
      for (let i = 0; i < num_grupos; i++) {
        novosGrupos.push({ letra: letras[i], times: [] });
      }
      setGruposManual(novosGrupos);
    }
    
    setShowGruposManualModal(true);
  };

  // Adicionar time a um grupo manualmente
  const adicionarTimeAoGrupo = (grupoLetra, timeId) => {
    setGruposManual(prev => prev.map(g => {
      if (g.letra === grupoLetra) {
        return { ...g, times: [...g.times, timeId] };
      }
      return g;
    }));
  };

  // Remover time de um grupo
  const removerTimeDoGrupo = (grupoLetra, timeId) => {
    setGruposManual(prev => prev.map(g => {
      if (g.letra === grupoLetra) {
        return { ...g, times: g.times.filter(t => t !== timeId) };
      }
      return g;
    }));
  };

  // Adicionar novo grupo
  const adicionarGrupoManual = () => {
    const letras = 'ABCDEFGH';
    const proximaLetra = letras[gruposManual.length] || `G${gruposManual.length + 1}`;
    setGruposManual(prev => [...prev, { letra: proximaLetra, times: [] }]);
  };

  // Remover grupo
  const removerGrupoManual = (grupoLetra) => {
    setGruposManual(prev => prev.filter(g => g.letra !== grupoLetra));
  };

  // Salvar configuração manual de grupos e gerar partidas
  const salvarGruposManual = async () => {
    // Verificar se todos os times estão em algum grupo
    const timesNosGrupos = gruposManual.flatMap(g => g.times);
    const timesTotais = torneioSelecionado.times.map(t => t.id);
    const timesSemGrupo = timesTotais.filter(t => !timesNosGrupos.includes(t));
    
    if (timesSemGrupo.length > 0) {
      toast.error(`${timesSemGrupo.length} time(s) não foram distribuídos nos grupos!`);
      return;
    }
    
    // Verificar se há grupos vazios
    const gruposVazios = gruposManual.filter(g => g.times.length === 0);
    if (gruposVazios.length > 0) {
      toast.error(`Há ${gruposVazios.length} grupo(s) vazio(s)!`);
      return;
    }
    
    // Montar grupos com dados completos dos times
    const grupos = gruposManual.map(g => ({
      letra: g.letra,
      times: g.times.map(tid => torneioSelecionado.times.find(t => t.id === tid))
    }));
    
    // Gerar partidas para cada grupo
    let todasPartidas = [];
    grupos.forEach(grupo => {
      const partidasGrupo = gerarPartidasGrupo(grupo.times, grupo.letra);
      todasPartidas = [...todasPartidas, ...partidasGrupo];
    });
    
    // Embaralhar partidas
    todasPartidas = embaralharPartidas(todasPartidas);
    
    try {
      await api.put(`/torneios-profissionais/${torneioSelecionado.id}`, {
        ...torneioSelecionado,
        grupos,
        partidas_grupos: todasPartidas,
        num_grupos: grupos.length,
        status: 'grupos'
      });
      
      setTorneioSelecionado(prev => ({
        ...prev,
        grupos,
        partidas_grupos: todasPartidas,
        num_grupos: grupos.length,
        status: 'grupos'
      }));
      
      toast.success(`Grupos configurados! ${todasPartidas.length} partidas geradas.`);
      setShowGruposManualModal(false);
      setActiveView('grupos');
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao salvar grupos');
    }
  };

  // Regenerar partidas mantendo os grupos atuais
  const handleRegenerarPartidas = async () => {
    if (!torneioSelecionado?.grupos?.length) {
      toast.error('Configure os grupos primeiro');
      return;
    }
    
    let todasPartidas = [];
    torneioSelecionado.grupos.forEach(grupo => {
      const partidasGrupo = gerarPartidasGrupo(grupo.times, grupo.letra);
      todasPartidas = [...todasPartidas, ...partidasGrupo];
    });
    
    todasPartidas = embaralharPartidas(todasPartidas);
    
    try {
      await api.put(`/torneios-profissionais/${torneioSelecionado.id}`, {
        ...torneioSelecionado,
        partidas_grupos: todasPartidas
      });
      
      setTorneioSelecionado(prev => ({
        ...prev,
        partidas_grupos: todasPartidas
      }));
      
      toast.success(`${todasPartidas.length} partidas regeneradas!`);
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao regenerar partidas');
    }
  };

  const abrirEditarPartida = (partida) => {
    setPartidaEditando(partida);
    setPlacar({ 
      placar1: partida.placar1 || 0, 
      placar2: partida.placar2 || 0,
      gols_time1: partida.gols_time1 || [],
      gols_time2: partida.gols_time2 || [],
      data: partida.data || '',
      horario: partida.horario || ''
    });
    setGolTemp1({ jogador_id: '', jogador_nome: '', quantidade: 1 });
    setGolTemp2({ jogador_id: '', jogador_nome: '', quantidade: 1 });
    setShowPartidaModal(true);
  };

  // Obter jogadores de um time pelo ID
  const getJogadoresTime = (timeId) => {
    if (!torneioSelecionado?.times) return [];
    const time = torneioSelecionado.times.find(t => t.id === timeId);
    return time?.jogadores || [];
  };

  const adicionarGol = (time) => {
    const golTemp = time === 1 ? golTemp1 : golTemp2;
    const setGolTemp = time === 1 ? setGolTemp1 : setGolTemp2;
    
    if (!golTemp.jogador_id) {
      toast.error('Selecione um jogador');
      return;
    }
    
    const novoGol = {
      jogador_id: golTemp.jogador_id,
      jogador_nome: golTemp.jogador_nome,
      quantidade: parseInt(golTemp.quantidade) || 1
    };
    
    if (time === 1) {
      const novoPlacar1 = placar.placar1 + novoGol.quantidade;
      setPlacar(prev => ({
        ...prev,
        placar1: novoPlacar1,
        gols_time1: [...prev.gols_time1, novoGol]
      }));
    } else {
      const novoPlacar2 = placar.placar2 + novoGol.quantidade;
      setPlacar(prev => ({
        ...prev,
        placar2: novoPlacar2,
        gols_time2: [...prev.gols_time2, novoGol]
      }));
    }
    
    setGolTemp({ jogador_id: '', jogador_nome: '', quantidade: 1 });
  };

  const removerGol = (time, index) => {
    if (time === 1) {
      const golRemovido = placar.gols_time1[index];
      setPlacar(prev => ({
        ...prev,
        placar1: prev.placar1 - (golRemovido?.quantidade || 1),
        gols_time1: prev.gols_time1.filter((_, i) => i !== index)
      }));
    } else {
      const golRemovido = placar.gols_time2[index];
      setPlacar(prev => ({
        ...prev,
        placar2: prev.placar2 - (golRemovido?.quantidade || 1),
        gols_time2: prev.gols_time2.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSalvarPlacar = async () => {
    if (!partidaEditando || !torneioSelecionado) return;
    
    setSalvando(true);
    try {
      const isMataMata = partidaEditando.fase !== 'grupos';
      const campo = isMataMata ? 'partidas_matamata' : 'partidas_grupos';
      
      const partidaAtualizada = {
        ...partidaEditando,
        placar1: placar.placar1,
        placar2: placar.placar2,
        gols_time1: placar.gols_time1,
        gols_time2: placar.gols_time2,
        data: placar.data,
        horario: placar.horario,
        status: 'finalizada'
      };
      
      const partidasAtualizadas = torneioSelecionado[campo].map(p => 
        p.id === partidaEditando.id ? partidaAtualizada : p
      );
      
      await api.put(`/torneios-profissionais/${torneioSelecionado.id}`, {
        ...torneioSelecionado,
        [campo]: partidasAtualizadas
      });
      
      setTorneioSelecionado(prev => ({
        ...prev,
        [campo]: partidasAtualizadas
      }));
      
      setShowPartidaModal(false);
      setPartidaEditando(null);
      toast.success('Placar salvo!');
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao salvar placar');
    } finally {
      setSalvando(false);
    }
  };

  const handleGerarMataMata = async () => {
    if (!torneioSelecionado) return;

    const classificadosPorGrupo = torneioSelecionado.classificados_por_grupo || 4;
    const gruposCfg = torneioSelecionado.grupos || [];
    const partidasGrupos = torneioSelecionado.partidas_grupos || [];

    // 1) Validar SOMENTE pelo status das partidas: cada grupo deve ter todas as partidas finalizadas
    const diagnostico = gruposCfg.map((g) => {
      const previstas = partidasGrupos.filter((p) => p.grupo === g.letra).length;
      const finalizadas = partidasGrupos.filter((p) => p.grupo === g.letra && p.status === 'finalizada').length;
      return { letra: g.letra, previstas, finalizadas, ok: previstas > 0 && finalizadas === previstas };
    });
    const pendentes = diagnostico.filter((d) => !d.ok);
    if (pendentes.length > 0 || diagnostico.length === 0) {
      const linhas = diagnostico.map((d) => `Grupo ${d.letra}: ${d.finalizadas}/${d.previstas} jogos finalizados`).join('\n');
      toast.error(`Mata-mata bloqueado.\n${linhas || 'Configure grupos e gere partidas primeiro.'}`, { duration: 7000 });
      return;
    }

    // 2) Classificados de cada grupo (respeitando config)
    const classificadosPorLetra = {};
    for (const g of gruposCfg) {
      classificadosPorLetra[g.letra] = calcularClassificacaoGrupo(torneioSelecionado, g.letra).slice(0, classificadosPorGrupo);
      if (classificadosPorLetra[g.letra].length < classificadosPorGrupo) {
        toast.error(`Grupo ${g.letra} não possui ${classificadosPorGrupo} times suficientes para classificação.`);
        return;
      }
    }

    // 3) Montar chaveamento. Atualmente otimizado para 2 grupos (A e B) com 2 ou 4 classificados.
    const letras = gruposCfg.map((g) => g.letra);
    const partidasMataMata = [];
    const mk = (id, fase, t1, t2, desc) => ({
      id, fase,
      time1_id: t1?.id ?? null, time2_id: t2?.id ?? null,
      time1_nome: t1?.nome ?? desc?.split(' x ')[0] ?? 'A definir',
      time2_nome: t2?.nome ?? desc?.split(' x ')[1] ?? 'A definir',
      placar1: 0, placar2: 0, gols_time1: [], gols_time2: [],
      status: (t1 && t2) ? 'agendada' : 'aguardando',
      descricao: desc || '',
    });

    if (letras.length === 2 && classificadosPorGrupo === 4) {
      const [LA, LB] = letras;
      const A = classificadosPorLetra[LA];
      const B = classificadosPorLetra[LB];
      partidasMataMata.push(
        mk('QF1', 'quartas', A[0], B[3], `1${LA} x 4${LB}`),
        mk('QF2', 'quartas', A[1], B[2], `2${LA} x 3${LB}`),
        mk('QF3', 'quartas', B[0], A[3], `1${LB} x 4${LA}`),
        mk('QF4', 'quartas', B[1], A[2], `2${LB} x 3${LA}`),
        mk('SF1', 'semi',    null, null, 'Venc. QF1 x Venc. QF2'),
        mk('SF2', 'semi',    null, null, 'Venc. QF3 x Venc. QF4'),
        mk('FINAL','final',  null, null, 'Venc. SF1 x Venc. SF2'),
      );
    } else if (letras.length === 2 && classificadosPorGrupo === 2) {
      const [LA, LB] = letras;
      const A = classificadosPorLetra[LA];
      const B = classificadosPorLetra[LB];
      partidasMataMata.push(
        mk('SF1', 'semi',   A[0], B[1], `1${LA} x 2${LB}`),
        mk('SF2', 'semi',   B[0], A[1], `1${LB} x 2${LA}`),
        mk('FINAL','final', null, null, 'Venc. SF1 x Venc. SF2'),
      );
    } else {
      toast.error(`Chaveamento automático suportado apenas para 2 grupos com 2 ou 4 classificados. Configuração atual: ${letras.length} grupos × ${classificadosPorGrupo} classificados.`);
      return;
    }

    try {
      await api.put(`/torneios-profissionais/${torneioSelecionado.id}`, {
        ...torneioSelecionado,
        partidas_matamata: partidasMataMata,
        status: 'matamata'
      });
      setTorneioSelecionado(prev => ({ ...prev, partidas_matamata: partidasMataMata, status: 'matamata' }));
      toast.success('Mata-mata gerado!');
      setActiveView('matamata');
      loadTorneios();
    } catch (error) {
      toast.error('Erro ao gerar mata-mata');
    }
  };

  // Impressão
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Torneio - ${torneioSelecionado?.nome}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .print-header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #6B21A8; }
          .print-header h1 { font-size: 18pt; color: #6B21A8; margin: 0 0 5px 0; }
          .print-header h2 { font-size: 14pt; color: #333; margin: 0; }
          .print-table { width: 100%; border-collapse: collapse; font-size: 10pt; margin: 15px 0; }
          .print-table th, .print-table td { border: 1px solid #333; padding: 6px 8px; text-align: center; }
          .print-table th { background-color: #6B21A8; color: white; }
          .section-title { font-size: 14pt; font-weight: bold; color: #6B21A8; margin: 20px 0 10px; }
          .print-footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 9pt; color: #666; text-align: center; }
          @media print { body { margin: 10px; } }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  // Baixar PDF do servidor
  const handleBaixarPDF = async (tipo = printType) => {
    if (!torneioSelecionado) return;
    
    setBaixandoPDF(true);
    try {
      const response = await api.post(`/torneios-profissionais/${torneioSelecionado.id}/pdf`, 
        { tipo },
        { responseType: 'blob' }
      );
      
      // Criar link de download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `torneio_${torneioSelecionado.nome.replace(/\s/g, '_')}_${tipo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast.error('Erro ao gerar PDF');
    } finally {
      setBaixandoPDF(false);
    }
  };

  // Enviar por e-mail
  const handleEnviarEmail = async () => {
    if (!torneioSelecionado || !emailDestinatarios.trim()) {
      toast.error('Informe pelo menos um e-mail destinatário');
      return;
    }
    
    const emails = emailDestinatarios.split(/[,;\s]+/).filter(e => e.includes('@'));
    if (emails.length === 0) {
      toast.error('E-mail inválido');
      return;
    }
    
    setEnviandoEmail(true);
    try {
      await api.post(`/torneios-profissionais/${torneioSelecionado.id}/email`, {
        tipo: printType,
        destinatarios: emails,
        assunto: emailAssunto || undefined,
        mensagem: emailMensagem || undefined
      });
      
      toast.success(`E-mail enviado para ${emails.length} destinatário(s)!`);
      setShowEmailModal(false);
      setEmailDestinatarios('');
      setEmailAssunto('');
      setEmailMensagem('');
    } catch (error) {
      console.error('Erro ao enviar e-mail:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar e-mail');
    } finally {
      setEnviandoEmail(false);
    }
  };

  // Editar nome do torneio
  const handleEditarNome = async () => {
    if (!novoNomeTorneio.trim()) {
      toast.error('Digite um nome válido');
      return;
    }
    
    setSalvando(true);
    try {
      const res = await api.patch(`/torneios-profissionais/${torneioSelecionado.id}`, {
        nome: novoNomeTorneio
      });
      
      setTorneioSelecionado(res.data);
      setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      toast.success('Nome atualizado!');
      setShowEditarNomeModal(false);
    } catch (error) {
      console.error('Erro ao atualizar nome:', error);
      toast.error('Erro ao atualizar nome');
    } finally {
      setSalvando(false);
    }
  };

  // Gerar cronograma com IA
  const handleGerarCronogramaIA = async () => {
    if (!torneioSelecionado?.partidas_grupos?.length) {
      toast.error('Primeiro faça o sorteio dos grupos para gerar partidas');
      return;
    }
    
    setGerandoCronograma(true);
    try {
      const res = await api.post(`/torneios-profissionais/${torneioSelecionado.id}/gerar-cronograma-ia`, cronogramaIA);
      
      setTorneioSelecionado(res.data.torneio);
      setTorneios(prev => prev.map(t => t.id === res.data.torneio.id ? res.data.torneio : t));
      toast.success(res.data.message);
      setShowCronogramaIAModal(false);
    } catch (error) {
      console.error('Erro ao gerar cronograma:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar cronograma');
    } finally {
      setGerandoCronograma(false);
    }
  };

  const getModalidade = (id) => MODALIDADES.find(m => m.id === id) || MODALIDADES[0];
  const getGenero = (id) => GENEROS.find(g => g.id === id) || GENEROS[0];

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/30">
              <Trophy className="w-9 h-9 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Torneio Profissional</h1>
              <p className="text-purple-300">Campeonatos com Grupos e Mata-Mata</p>
            </div>
          </div>
          <Button onClick={() => setShowCriarModal(true)} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700">
            <Plus className="w-5 h-5 mr-2" />Novo Torneio
          </Button>
        </div>

        {/* Lista ou Torneio Selecionado */}
        {!torneioSelecionado ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {torneios.length === 0 ? (
              <Card className="col-span-full bg-white/5 border-white/10">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Trophy className="w-20 h-20 text-purple-400/30 mb-4" />
                  <h3 className="text-xl text-white mb-2">Nenhum torneio criado</h3>
                  <p className="text-purple-300 text-center mb-4">Crie seu primeiro torneio profissional</p>
                  <Button onClick={() => setShowCriarModal(true)} className="bg-purple-600">
                    <Plus className="w-4 h-4 mr-2" />Criar Torneio
                  </Button>
                </CardContent>
              </Card>
            ) : (
              torneios.map(torneio => {
                const modalidade = getModalidade(torneio.modalidade);
                const genero = getGenero(torneio.genero);
                return (
                  <Card key={torneio.id} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all cursor-pointer group"
                    onClick={() => { setTorneioSelecionado(torneio); setActiveView(torneio.status === 'configurando' ? 'admin' : 'grupos'); }}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{modalidade.icon}</span>
                          <div>
                            <CardTitle className="text-white group-hover:text-yellow-400">{torneio.nome}</CardTitle>
                            <CardDescription className="text-purple-300">{modalidade.nome} {genero.icon}</CardDescription>
                          </div>
                        </div>
                        <Badge className={`${torneio.status === 'configurando' ? 'bg-yellow-500/20 text-yellow-400' : torneio.status === 'grupos' ? 'bg-blue-500/20 text-blue-400' : torneio.status === 'matamata' ? 'bg-purple-500/20 text-purple-400' : 'bg-green-500/20 text-green-400'}`}>
                          {torneio.status === 'configurando' && 'Configurando'}
                          {torneio.status === 'grupos' && 'Fase de Grupos'}
                          {torneio.status === 'matamata' && 'Mata-Mata'}
                          {torneio.status === 'finalizado' && 'Finalizado'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{torneio.times?.length || 0}</p>
                          <p className="text-xs text-purple-300">Times</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{torneio.num_grupos || 2}</p>
                          <p className="text-xs text-purple-300">Grupos</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{torneio.partidas_grupos?.length || 0}</p>
                          <p className="text-xs text-purple-300">Partidas</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button variant="ghost" className="flex-1 text-purple-300 hover:text-white hover:bg-white/10">
                          <Eye className="w-4 h-4 mr-2" />Ver
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={(e) => { e.stopPropagation(); handleExcluirTorneio(torneio.id); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header do Torneio */}
            <Card className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-purple-500/30">
              <CardContent className="py-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => setTorneioSelecionado(null)} className="text-white hover:bg-white/10">← Voltar</Button>
                    <span className="text-4xl">{getModalidade(torneioSelecionado.modalidade).icon}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-bold text-white">{torneioSelecionado.nome}</h2>
                        <Button variant="ghost" size="sm" onClick={() => { setNovoNomeTorneio(torneioSelecionado.nome); setShowEditarNomeModal(true); }} className="text-purple-300 hover:text-white h-7 w-7 p-0">
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-purple-300">{getModalidade(torneioSelecionado.modalidade).nome} {getGenero(torneioSelecionado.genero).icon} {getGenero(torneioSelecionado.genero).nome}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {torneioSelecionado.partidas_grupos?.length > 0 && (
                      <Button variant="outline" onClick={() => setShowCronogramaIAModal(true)} className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10">
                        <Sparkles className="w-4 h-4 mr-2" />Gerar Horários com IA
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => { setPrintType('partidas'); setShowPrintModal(true); }} className="border-white/20 text-white hover:bg-white/10">
                      <Printer className="w-4 h-4 mr-2" />Imprimir
                    </Button>
                    <Button variant="outline" onClick={() => handleExcluirTorneio(torneioSelecionado.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4 mr-2" />Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeView} onValueChange={setActiveView}>
              <TabsList className="bg-white/5 border border-white/10 p-1 w-full justify-start flex-wrap">
                <TabsTrigger value="admin" className="data-[state=active]:bg-purple-600 text-purple-300">
                  <Settings className="w-4 h-4 mr-2" />Configurar
                </TabsTrigger>
                <TabsTrigger value="grupos" className="data-[state=active]:bg-blue-600 text-purple-300" disabled={torneioSelecionado.status === 'configurando'}>
                  <Users className="w-4 h-4 mr-2" />Grupos
                </TabsTrigger>
                <TabsTrigger value="matamata" className="data-[state=active]:bg-red-600 text-purple-300" disabled={!torneioSelecionado.partidas_matamata?.length}>
                  <Swords className="w-4 h-4 mr-2" />Mata-Mata
                </TabsTrigger>
                <TabsTrigger value="artilheiros" className="data-[state=active]:bg-yellow-600 text-purple-300">
                  <Goal className="w-4 h-4 mr-2" />Artilheiros
                </TabsTrigger>
              </TabsList>

              {/* TAB: CONFIGURAR */}
              <TabsContent value="admin" className="mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white flex items-center gap-2">
                          <Shield className="w-5 h-5 text-blue-400" />Times ({torneioSelecionado.times?.length || 0})
                        </CardTitle>
                        <Button size="sm" onClick={() => abrirModalTime()} className="bg-green-600">
                          <Plus className="w-4 h-4 mr-1" />Adicionar
                        </Button>
                      </div>
                      <CardDescription className="text-purple-300">
                        Necessário: {torneioSelecionado.num_grupos * torneioSelecionado.times_por_grupo} times
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {torneioSelecionado.times?.map((time, idx) => (
                          <div key={time.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">{idx + 1}</span>
                              <div>
                                <span className="text-white font-medium">{time.nome}</span>
                                {time.jogadores?.length > 0 && (
                                  <p className="text-xs text-purple-300">{time.jogadores.length} jogadores</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => abrirModalTime(time)} className="text-blue-400 hover:bg-blue-500/10">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleRemoverTime(time.id)} className="text-red-400 hover:bg-red-500/10">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {(!torneioSelecionado.times || torneioSelecionado.times.length === 0) && (
                          <p className="text-center text-purple-300 py-8">Nenhum time cadastrado</p>
                        )}
                      </div>
                      
                      {torneioSelecionado.times?.length >= 2 && (
                        <div className="space-y-2 mt-4">
                          {torneioSelecionado.status === 'configurando' && (
                            <>
                              <Button onClick={handleDistribuirGrupos} className="w-full bg-gradient-to-r from-purple-600 to-blue-600">
                                <Zap className="w-4 h-4 mr-2" />Sortear Aleatoriamente
                              </Button>
                              <Button onClick={abrirConfigGruposManual} variant="outline" className="w-full border-purple-500/30 text-purple-300">
                                <Settings className="w-4 h-4 mr-2" />Configurar Grupos Manualmente
                              </Button>
                            </>
                          )}
                          {torneioSelecionado.status === 'grupos' && (
                            <>
                              <Button onClick={abrirConfigGruposManual} variant="outline" className="w-full border-purple-500/30 text-purple-300">
                                <Edit className="w-4 h-4 mr-2" />Editar Grupos
                              </Button>
                              <Button onClick={handleRegenerarPartidas} variant="outline" className="w-full border-blue-500/30 text-blue-300">
                                <RefreshCw className="w-4 h-4 mr-2" />Regenerar Partidas
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-white/5 border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Settings className="w-5 h-5 text-purple-400" />Configurações
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-purple-300">Nº de Grupos</Label>
                          <Input 
                            type="number" 
                            min="1" 
                            max="8"
                            value={torneioSelecionado.num_grupos} 
                            onChange={async (e) => {
                              const val = parseInt(e.target.value) || 2;
                              try {
                                const res = await api.patch(`/torneios-profissionais/${torneioSelecionado.id}`, { num_grupos: val });
                                setTorneioSelecionado(res.data);
                                setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
                              } catch (err) { console.error(err); }
                            }}
                            className="bg-white/10 border-white/20 text-white" 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-purple-300">Times por Grupo</Label>
                          <Input 
                            type="number" 
                            min="2" 
                            max="10"
                            value={torneioSelecionado.times_por_grupo} 
                            onChange={async (e) => {
                              const val = parseInt(e.target.value) || 4;
                              try {
                                const res = await api.patch(`/torneios-profissionais/${torneioSelecionado.id}`, { times_por_grupo: val });
                                setTorneioSelecionado(res.data);
                                setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
                              } catch (err) { console.error(err); }
                            }}
                            className="bg-white/10 border-white/20 text-white" 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-purple-300">Classificados</Label>
                          <Input 
                            type="number" 
                            min="1" 
                            max="4"
                            value={torneioSelecionado.classificados_por_grupo || 2} 
                            onChange={async (e) => {
                              const val = parseInt(e.target.value) || 2;
                              try {
                                const res = await api.patch(`/torneios-profissionais/${torneioSelecionado.id}`, { classificados_por_grupo: val });
                                setTorneioSelecionado(res.data);
                                setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
                              } catch (err) { console.error(err); }
                            }}
                            className="bg-white/10 border-white/20 text-white" 
                          />
                        </div>
                      </div>
                      
                      <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                        <h4 className="text-blue-400 font-semibold mb-2">📋 Regras</h4>
                        <ul className="text-sm text-purple-300 space-y-1">
                          <li>• Vitória = 3 pts | Empate = 1 pt | Derrota = 0 pt</li>
                          <li>• Times necessários: {torneioSelecionado.num_grupos * torneioSelecionado.times_por_grupo}</li>
                        </ul>
                        <h4 className="text-blue-400 font-semibold mt-3 mb-2">🎯 Desempate</h4>
                        <p className="text-sm text-purple-300">Pontos → Vitórias → Saldo → Gols → Confronto → Sorteio</p>
                      </div>
                      
                      {/* Upload de Logo */}
                      <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                        <h4 className="text-purple-400 font-semibold mb-3">🏫 Logo da Escola</h4>
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden border border-white/20">
                            {schoolLogo ? (
                              <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-purple-400 text-2xl">📷</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-purple-300 mb-2">
                              {schoolLogo ? 'Logo configurado' : 'Nenhum logo configurado'}
                            </p>
                            <label className="cursor-pointer">
                              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                              <Button variant="outline" size="sm" asChild className="border-purple-500/30 text-purple-300 hover:bg-purple-500/20">
                                <span>{schoolLogo ? 'Trocar Logo' : 'Upload Logo'}</span>
                              </Button>
                            </label>
                          </div>
                        </div>
                        <p className="text-xs text-purple-400 mt-2">Este logo aparecerá em todos os documentos impressos</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* TAB: GRUPOS */}
              <TabsContent value="grupos" className="mt-6">
                {torneioSelecionado.grupos?.length > 0 ? (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {torneioSelecionado.grupos.map(grupo => {
                        const classificacao = calcularClassificacaoGrupo(torneioSelecionado, grupo.letra);
                        const partidasGrupo = torneioSelecionado.partidas_grupos?.filter(p => p.grupo === grupo.letra) || [];
                        
                        return (
                          <Card key={grupo.letra} className="bg-white/5 border-white/10 overflow-hidden">
                            <CardHeader className="bg-gradient-to-r from-blue-600/30 to-purple-600/30 pb-3">
                              <CardTitle className="text-white flex items-center gap-2">
                                <span className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-xl font-bold">{grupo.letra}</span>
                                Grupo {grupo.letra}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-white/5 text-purple-300">
                                      <th className="p-2 text-left">#</th>
                                      <th className="p-2 text-left">Time</th>
                                      <th className="p-2 text-center">P</th>
                                      <th className="p-2 text-center">J</th>
                                      <th className="p-2 text-center">V</th>
                                      <th className="p-2 text-center">E</th>
                                      <th className="p-2 text-center">D</th>
                                      <th className="p-2 text-center">SG</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {classificacao.map((time, idx) => (
                                      <tr key={time.id} className={`border-b border-white/5 ${idx < 4 ? 'bg-green-500/10' : ''}`}>
                                        <td className="p-2 text-white font-bold">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}º`}</td>
                                        <td className="p-2 text-white font-medium">{time.nome}</td>
                                        <td className="p-2 text-center text-yellow-400 font-bold">{time.pontos}</td>
                                        <td className="p-2 text-center text-purple-300">{time.jogos}</td>
                                        <td className="p-2 text-center text-green-400">{time.vitorias}</td>
                                        <td className="p-2 text-center text-purple-300">{time.empates}</td>
                                        <td className="p-2 text-center text-red-400">{time.derrotas}</td>
                                        <td className="p-2 text-center text-white">{time.saldo > 0 ? '+' : ''}{time.saldo}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              
                              <div className="p-4 space-y-2">
                                <h4 className="text-purple-300 text-sm font-semibold mb-3">Partidas</h4>
                                {partidasGrupo.map(partida => (
                                  <div key={partida.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 cursor-pointer" onClick={() => abrirEditarPartida(partida)}>
                                    <span className="text-white text-sm flex-1 text-right truncate pr-3">{partida.time1_nome}</span>
                                    <div className="flex items-center gap-2 px-4 py-1 bg-slate-800 rounded-lg min-w-[80px] justify-center">
                                      <span className={`text-lg font-bold ${partida.placar1 > partida.placar2 ? 'text-green-400' : 'text-white'}`}>{partida.placar1}</span>
                                      <span className="text-purple-400">×</span>
                                      <span className={`text-lg font-bold ${partida.placar2 > partida.placar1 ? 'text-green-400' : 'text-white'}`}>{partida.placar2}</span>
                                    </div>
                                    <span className="text-white text-sm flex-1 truncate pl-3">{partida.time2_nome}</span>
                                    <div className="ml-2 flex flex-col items-end">
                                      <Badge className={partida.status === 'finalizada' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                        {partida.status === 'finalizada' ? 'Fim' : 'Pend'}
                                      </Badge>
                                      {partida.data && <span className="text-xs text-purple-300 mt-1">{partida.data} {partida.horario}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    
                    {/* Cronograma Editável */}
                    <Card className="bg-white/5 border-white/10">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-green-400" />Cronograma de Partidas
                          </CardTitle>
                          <Button variant="outline" size="sm" onClick={() => setShowCronogramaIAModal(true)} className="border-purple-500/30 text-purple-300">
                            <Sparkles className="w-4 h-4 mr-1" />Gerar com IA
                          </Button>
                        </div>
                        <CardDescription className="text-purple-300">
                          Use as setas para mover jogos ou edite data/hora manualmente
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-white/5 text-purple-300">
                                <th className="p-2 text-center w-16">Mover</th>
                                <th className="p-2 text-center">#</th>
                                <th className="p-2 text-center">Data</th>
                                <th className="p-2 text-center">Hora</th>
                                <th className="p-2 text-center">Grupo</th>
                                <th className="p-2 text-left">Partida</th>
                                <th className="p-2 text-center">Placar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...(torneioSelecionado?.partidas_grupos || [])]
                                .sort((a, b) => {
                                  if (a.ordem && b.ordem) return a.ordem - b.ordem;
                                  const dataA = `${a.data || '9999-99-99'}T${a.horario || '99:99'}`;
                                  const dataB = `${b.data || '9999-99-99'}T${b.horario || '99:99'}`;
                                  return dataA.localeCompare(dataB);
                                })
                                .map((partida, idx, arr) => (
                                  <tr key={partida.id} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="p-1 text-center">
                                      <div className="flex flex-col gap-0.5">
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-6 w-6 p-0 text-purple-300 hover:text-white hover:bg-purple-500/20"
                                          disabled={idx === 0}
                                          onClick={async () => {
                                            try {
                                              const res = await api.post(`/torneios-profissionais/${torneioSelecionado.id}/partidas/${partida.id}/mover`, { direcao: 'cima' });
                                              setTorneioSelecionado(res.data);
                                              setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
                                            } catch (err) { console.error(err); }
                                          }}
                                        >
                                          <ChevronUp className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-6 w-6 p-0 text-purple-300 hover:text-white hover:bg-purple-500/20"
                                          disabled={idx === arr.length - 1}
                                          onClick={async () => {
                                            try {
                                              const res = await api.post(`/torneios-profissionais/${torneioSelecionado.id}/partidas/${partida.id}/mover`, { direcao: 'baixo' });
                                              setTorneioSelecionado(res.data);
                                              setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
                                            } catch (err) { console.error(err); }
                                          }}
                                        >
                                          <ChevronDown className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </td>
                                    <td className="p-2 text-center text-purple-400 font-bold">{partida.ordem || idx + 1}</td>
                                    <td className="p-2 text-center">
                                      <Input
                                        type="date"
                                        value={partida.data || ''}
                                        onChange={async (e) => {
                                          try {
                                            const res = await api.patch(`/torneios-profissionais/${torneioSelecionado.id}/partidas/${partida.id}`, { data: e.target.value });
                                            setTorneioSelecionado(res.data);
                                            setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
                                          } catch (err) { console.error(err); }
                                        }}
                                        className="bg-transparent border-white/20 text-white text-xs h-8 w-32"
                                      />
                                    </td>
                                    <td className="p-2 text-center">
                                      <Input
                                        type="time"
                                        value={partida.horario || ''}
                                        onChange={async (e) => {
                                          try {
                                            const res = await api.patch(`/torneios-profissionais/${torneioSelecionado.id}/partidas/${partida.id}`, { horario: e.target.value });
                                            setTorneioSelecionado(res.data);
                                            setTorneios(prev => prev.map(t => t.id === res.data.id ? res.data : t));
                                          } catch (err) { console.error(err); }
                                        }}
                                        className="bg-transparent border-white/20 text-white text-xs h-8 w-24"
                                      />
                                    </td>
                                    <td className="p-2 text-center">
                                      <Badge className="bg-blue-500/20 text-blue-400">{partida.grupo}</Badge>
                                    </td>
                                    <td className="p-2 text-white">
                                      {partida.time1_nome} vs {partida.time2_nome}
                                    </td>
                                    <td className="p-2 text-center">
                                      <span className="font-bold text-white">{partida.placar1} × {partida.placar2}</span>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {torneioSelecionado.status === 'grupos' && (
                      <div className="flex justify-center">
                        <Button onClick={handleGerarMataMata} className="bg-gradient-to-r from-red-500 to-orange-500 text-lg px-8 py-6">
                          <Swords className="w-5 h-5 mr-2" />Gerar Mata-Mata
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <Users className="w-16 h-16 text-purple-400/30 mb-4" />
                      <h3 className="text-xl text-white mb-2">Grupos não sorteados</h3>
                      <p className="text-purple-300">Cadastre os times e sorteie na aba "Configurar"</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* TAB: MATA-MATA */}
              <TabsContent value="matamata" className="mt-6">
                {torneioSelecionado.partidas_matamata?.length > 0 ? (
                  <div className="space-y-8">
                    <div className="text-center">
                      <h2 className="text-3xl font-bold text-white mb-2">🏆 FASE ELIMINATÓRIA</h2>
                    </div>
                    
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Quartas */}
                        <div className="space-y-4">
                          <h3 className="text-center text-yellow-400 font-bold text-lg mb-4">⚔️ QUARTAS</h3>
                          {torneioSelecionado.partidas_matamata.filter(p => p.fase === 'quartas').map(partida => (
                            <div key={partida.id} className="bg-slate-800/50 rounded-lg p-4 border border-white/10 hover:border-yellow-500/50 cursor-pointer" onClick={() => abrirEditarPartida(partida)}>
                              <div className="text-xs text-purple-400 mb-2 text-center">{partida.descricao || partida.id}</div>
                              <div className="space-y-2">
                                <div className={`flex justify-between items-center p-2 rounded ${partida.placar1 > partida.placar2 && partida.status === 'finalizada' ? 'bg-green-500/20' : ''}`}>
                                  <span className="text-white text-sm truncate">{partida.time1_nome}</span>
                                  <span className="text-white font-bold">{partida.placar1}</span>
                                </div>
                                <div className={`flex justify-between items-center p-2 rounded ${partida.placar2 > partida.placar1 && partida.status === 'finalizada' ? 'bg-green-500/20' : ''}`}>
                                  <span className="text-white text-sm truncate">{partida.time2_nome}</span>
                                  <span className="text-white font-bold">{partida.placar2}</span>
                                </div>
                              </div>
                              {partida.data && <p className="text-xs text-purple-300 text-center mt-2">{partida.data} {partida.horario}</p>}
                            </div>
                          ))}
                        </div>
                        
                        {/* Semis */}
                        <div className="space-y-4 md:mt-16">
                          <h3 className="text-center text-orange-400 font-bold text-lg mb-4">🔥 SEMIFINAIS</h3>
                          {torneioSelecionado.partidas_matamata.filter(p => p.fase === 'semi').map(partida => (
                            <div key={partida.id} className="bg-slate-800/50 rounded-lg p-4 border border-white/10 cursor-pointer" onClick={() => partida.status === 'agendada' && abrirEditarPartida(partida)}>
                              <div className="space-y-2">
                                <div className={`flex justify-between items-center p-2 rounded ${partida.placar1 > partida.placar2 && partida.status === 'finalizada' ? 'bg-green-500/20' : ''}`}>
                                  <span className="text-white text-sm truncate">{partida.time1_nome}</span>
                                  <span className="text-white font-bold">{partida.placar1}</span>
                                </div>
                                <div className={`flex justify-between items-center p-2 rounded ${partida.placar2 > partida.placar1 && partida.status === 'finalizada' ? 'bg-green-500/20' : ''}`}>
                                  <span className="text-white text-sm truncate">{partida.time2_nome}</span>
                                  <span className="text-white font-bold">{partida.placar2}</span>
                                </div>
                              </div>
                              <Badge className={`mt-2 w-full justify-center ${partida.status === 'finalizada' ? 'bg-green-500/20 text-green-400' : partida.status === 'aguardando' ? 'bg-slate-500/20 text-slate-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                {partida.status === 'finalizada' ? '✓ Fim' : partida.status === 'aguardando' ? '⏳ Aguardando' : 'Pendente'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                        
                        {/* Final */}
                        <div className="md:col-span-2 flex items-center justify-center">
                          <div className="w-full max-w-md">
                            <h3 className="text-center text-yellow-400 font-bold text-xl mb-4">🏆 GRANDE FINAL</h3>
                            {torneioSelecionado.partidas_matamata.filter(p => p.fase === 'final').map(partida => (
                              <div key={partida.id} className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl p-6 border-2 border-yellow-500/50 cursor-pointer" onClick={() => partida.status === 'agendada' && abrirEditarPartida(partida)}>
                                <div className="text-center mb-4">
                                  <Trophy className="w-12 h-12 text-yellow-400 mx-auto" />
                                </div>
                                <div className="space-y-3">
                                  <div className={`flex justify-between items-center p-3 rounded-lg ${partida.placar1 > partida.placar2 && partida.status === 'finalizada' ? 'bg-green-500/30 ring-2 ring-green-400' : 'bg-white/5'}`}>
                                    <span className="text-white font-semibold">{partida.time1_nome}</span>
                                    <span className="text-2xl font-bold text-white">{partida.placar1}</span>
                                  </div>
                                  <div className="text-center text-purple-400">VS</div>
                                  <div className={`flex justify-between items-center p-3 rounded-lg ${partida.placar2 > partida.placar1 && partida.status === 'finalizada' ? 'bg-green-500/30 ring-2 ring-green-400' : 'bg-white/5'}`}>
                                    <span className="text-white font-semibold">{partida.time2_nome}</span>
                                    <span className="text-2xl font-bold text-white">{partida.placar2}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Campeão */}
                    {(() => {
                      const final = torneioSelecionado.partidas_matamata.find(p => p.fase === 'final');
                      if (final?.status === 'finalizada') {
                        const campeao = final.placar1 > final.placar2 ? final.time1_nome : final.time2_nome;
                        return (
                          <div className="text-center py-8 bg-gradient-to-r from-yellow-500/20 via-yellow-400/30 to-yellow-500/20 rounded-xl border border-yellow-500/50">
                            <Crown className="w-20 h-20 text-yellow-400 mx-auto mb-4 animate-bounce" />
                            <h2 className="text-4xl font-bold text-yellow-400 mb-2">🏆 CAMPEÃO 🏆</h2>
                            <p className="text-3xl font-bold text-white">{campeao}</p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <Swords className="w-16 h-16 text-purple-400/30 mb-4" />
                      <h3 className="text-xl text-white mb-2">Mata-mata não iniciado</h3>
                      <p className="text-purple-300">Complete a fase de grupos primeiro</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* TAB: ARTILHEIROS */}
              <TabsContent value="artilheiros" className="mt-6">
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Goal className="w-5 h-5 text-yellow-400" />Artilheiros do Torneio
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const artilheiros = calcularArtilheiros(torneioSelecionado);
                      if (artilheiros.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <Goal className="w-16 h-16 text-purple-400/30 mx-auto mb-4" />
                            <p className="text-purple-300">Nenhum gol marcado ainda</p>
                          </div>
                        );
                      }
                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="text-purple-300 border-b border-white/10">
                                <th className="p-3 text-left">#</th>
                                <th className="p-3 text-left">Jogador</th>
                                <th className="p-3 text-left">Time</th>
                                <th className="p-3 text-center">Gols</th>
                              </tr>
                            </thead>
                            <tbody>
                              {artilheiros.map((art, idx) => (
                                <tr key={idx} className={`border-b border-white/5 ${idx === 0 ? 'bg-yellow-500/10' : ''}`}>
                                  <td className="p-3 text-white font-bold">
                                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}º`}
                                  </td>
                                  <td className="p-3 text-white font-medium">{art.nome}</td>
                                  <td className="p-3 text-purple-300">{art.time_nome}</td>
                                  <td className="p-3 text-center">
                                    <span className="text-2xl font-bold text-yellow-400">{art.gols}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* MODAIS */}
      
      {/* Modal Criar Torneio */}
      <Dialog open={showCriarModal} onOpenChange={setShowCriarModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />Criar Novo Torneio
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-purple-300">Nome do Torneio</Label>
              <Input placeholder="Ex: Copa Interclasse 2026" value={formTorneio.nome} onChange={(e) => setFormTorneio({...formTorneio, nome: e.target.value})} className="bg-white/10 border-white/20 text-white" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-purple-300">Modalidade</Label>
                <Select value={formTorneio.modalidade} onValueChange={(v) => setFormTorneio({...formTorneio, modalidade: v})}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODALIDADES.map(m => (<SelectItem key={m.id} value={m.id}>{m.icon} {m.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-purple-300">Gênero</Label>
                <Select value={formTorneio.genero} onValueChange={(v) => setFormTorneio({...formTorneio, genero: v})}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GENEROS.map(g => (<SelectItem key={g.id} value={g.id}>{g.icon} {g.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-purple-300">Nº Grupos</Label>
                <Select value={String(formTorneio.num_grupos)} onValueChange={(v) => setFormTorneio({...formTorneio, num_grupos: parseInt(v)})}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="8">8</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-purple-300">Times/Grupo</Label>
                <Select value={String(formTorneio.times_por_grupo)} onValueChange={(v) => setFormTorneio({...formTorneio, times_por_grupo: parseInt(v)})}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="6">6</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-purple-300">Classif.</Label>
                <Select value={String(formTorneio.classificados_por_grupo)} onValueChange={(v) => setFormTorneio({...formTorneio, classificados_por_grupo: parseInt(v)})}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <p className="text-sm text-blue-300">
                <strong>Total de times:</strong> {formTorneio.num_grupos * formTorneio.times_por_grupo} | 
                <strong> Partidas grupos:</strong> {formTorneio.num_grupos * (formTorneio.times_por_grupo * (formTorneio.times_por_grupo - 1) / 2)}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCriarModal(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleCriarTorneio} disabled={salvando} className="bg-green-600">
              {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Time */}
      <Dialog open={showTimeModal} onOpenChange={setShowTimeModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />{timeEditando ? 'Editar Time' : 'Novo Time'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-purple-300">Nome do Time</Label>
              <Input placeholder="Ex: 6º Ano A" value={formTime.nome} onChange={(e) => setFormTime({...formTime, nome: e.target.value})} className="bg-white/10 border-white/20 text-white" />
            </div>
            
            {/* Modo de seleção de alunos */}
            <div className="space-y-2">
              <Label className="text-purple-300">Selecionar jogadores de:</Label>
              <div className="flex gap-2">
                <Button 
                  variant={modoSelecaoAlunos === 'turma' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => { 
                    setModoSelecaoAlunos('turma'); 
                    setAlunosDisponiveis([]);
                    if (formTime.turma_id) loadAlunosTurma(formTime.turma_id);
                  }}
                  className={modoSelecaoAlunos === 'turma' ? 'bg-purple-600' : 'border-white/20 text-white'}
                >
                  <Users className="w-4 h-4 mr-1" />Turma Específica
                </Button>
                <Button 
                  variant={modoSelecaoAlunos === 'todos' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => { 
                    setModoSelecaoAlunos('todos'); 
                    loadTodosAlunos();
                  }}
                  className={modoSelecaoAlunos === 'todos' ? 'bg-green-600' : 'border-white/20 text-white'}
                >
                  <Users className="w-4 h-4 mr-1" />Todas as Turmas
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {modoSelecaoAlunos === 'turma' && (
                <div className="space-y-2">
                  <Label className="text-purple-300">Turma</Label>
                  <Select value={formTime.turma_id} onValueChange={(v) => { setFormTime({...formTime, turma_id: v}); loadAlunosTurma(v); }}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue placeholder="Selecione a turma..." /></SelectTrigger>
                    <SelectContent>
                      {turmas.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className={`space-y-2 ${modoSelecaoAlunos === 'todos' ? 'col-span-2' : ''}`}>
                <Label className="text-purple-300">Jogadores Selecionados</Label>
                <div className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white text-center font-semibold">
                  {formTime.jogadores.length} selecionados
                </div>
              </div>
            </div>
            
            {/* Lista de alunos - mostra quando tem alunos disponíveis */}
            {alunosDisponiveis.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-purple-300">
                    {modoSelecaoAlunos === 'todos' ? `Todos os alunos (${alunosDisponiveis.length})` : 'Selecione os jogadores'}
                  </Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      const filtered = alunosDisponiveis.filter(a => a.name.toLowerCase().includes(buscaAluno.toLowerCase()));
                      setFormTime({...formTime, jogadores: filtered.map(a => ({id: a.id, nome: a.name}))});
                    }} className="border-green-500/30 text-green-400 text-xs h-7 px-2">
                      Todos
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setFormTime({...formTime, jogadores: []})} className="border-red-500/30 text-red-400 text-xs h-7 px-2">
                      Limpar
                    </Button>
                  </div>
                </div>
                <Input placeholder="Buscar aluno..." value={buscaAluno} onChange={(e) => setBuscaAluno(e.target.value)} className="bg-white/10 border-white/20 text-white" />
                <div className="flex flex-wrap gap-2 p-3 border border-white/10 rounded-lg bg-white/5 max-h-64 overflow-y-auto">
                  {alunosDisponiveis
                    .filter(a => a.name.toLowerCase().includes(buscaAluno.toLowerCase()))
                    .slice(0, 200) // Limitar para performance
                    .map(aluno => {
                      const isSelected = formTime.jogadores.some(j => j.id === aluno.id);
                      return (
                        <button
                          key={aluno.id}
                          type="button"
                          onClick={() => toggleJogadorTime(aluno)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                            isSelected 
                              ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30' 
                              : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/20'
                          }`}
                        >
                          {aluno.name} {modoSelecaoAlunos === 'todos' && aluno.class_name ? <span className="text-[10px] opacity-70">({aluno.class_name.split(' ')[0]})</span> : ''}
                        </button>
                      );
                    })}
                </div>
                {alunosDisponiveis.length > 0 && (
                  <p className="text-xs text-purple-400 text-center">
                    Clique nos nomes para selecionar/deselecionar os jogadores do time
                  </p>
                )}
              </div>
            )}
            
            {modoSelecaoAlunos === 'turma' && formTime.turma_id && alunosDisponiveis.length === 0 && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                <p className="text-yellow-400 text-sm">Nenhum aluno encontrado nesta turma.</p>
              </div>
            )}
            
            {modoSelecaoAlunos === 'turma' && !formTime.turma_id && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                <p className="text-blue-400 text-sm">Selecione uma turma para ver os alunos disponíveis.</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeModal(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleSalvarTime} className="bg-green-600">
              <Save className="w-4 h-4 mr-2" />Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Partida/Placar */}
      <Dialog open={showPartidaModal} onOpenChange={setShowPartidaModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Swords className="w-5 h-5 text-purple-400" />Registrar Partida
            </DialogTitle>
          </DialogHeader>
          
          {partidaEditando && (
            <div className="space-y-6 py-4">
              <div className="text-center">
                <Badge>{partidaEditando.fase === 'grupos' ? `Grupo ${partidaEditando.grupo}` : partidaEditando.fase?.toUpperCase()}</Badge>
              </div>
              
              {/* Data e Horário */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-purple-300">Data</Label>
                  <Input type="date" value={placar.data} onChange={(e) => setPlacar({...placar, data: e.target.value})} className="bg-white/10 border-white/20 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-purple-300">Horário</Label>
                  <Input type="time" value={placar.horario} onChange={(e) => setPlacar({...placar, horario: e.target.value})} className="bg-white/10 border-white/20 text-white" />
                </div>
              </div>
              
              {/* Placar */}
              <div className="grid grid-cols-3 gap-4 items-center">
                <div className="text-center">
                  <p className="text-white font-semibold mb-2">{partidaEditando.time1_nome}</p>
                  <div className="text-4xl font-bold text-white bg-white/10 rounded-lg py-4">{placar.placar1}</div>
                </div>
                <div className="text-center text-3xl text-purple-400 font-bold">×</div>
                <div className="text-center">
                  <p className="text-white font-semibold mb-2">{partidaEditando.time2_nome}</p>
                  <div className="text-4xl font-bold text-white bg-white/10 rounded-lg py-4">{placar.placar2}</div>
                </div>
              </div>
              
              {/* Gols Time 1 */}
              <div className="space-y-3">
                <h4 className="text-purple-300 font-semibold">⚽ Gols de {partidaEditando.time1_nome}</h4>
                <div className="space-y-2">
                  {placar.gols_time1.map((gol, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded">
                      <span className="text-white">{gol.jogador_nome} ({gol.quantidade})</span>
                      <Button variant="ghost" size="sm" onClick={() => removerGol(1, idx)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  {getJogadoresTime(partidaEditando.time1_id).length > 0 ? (
                    <Select 
                      value={golTemp1.jogador_id} 
                      onValueChange={(v) => {
                        const jogador = getJogadoresTime(partidaEditando.time1_id).find(j => j.id === v);
                        setGolTemp1({...golTemp1, jogador_id: v, jogador_nome: jogador?.nome || ''});
                      }}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white flex-1">
                        <SelectValue placeholder="Selecione o jogador..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getJogadoresTime(partidaEditando.time1_id).map(j => (
                          <SelectItem key={j.id} value={j.id}>{j.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input placeholder="Nome do jogador (sem elenco cadastrado)" value={golTemp1.jogador_nome} onChange={(e) => setGolTemp1({...golTemp1, jogador_id: `manual-${Date.now()}`, jogador_nome: e.target.value})} className="bg-white/10 border-white/20 text-white flex-1" />
                  )}
                  <Input type="number" min="1" value={golTemp1.quantidade} onChange={(e) => setGolTemp1({...golTemp1, quantidade: parseInt(e.target.value) || 1})} className="bg-white/10 border-white/20 text-white w-16" />
                  <Button onClick={() => adicionarGol(1)} className="bg-green-600"><Plus className="w-4 h-4" /></Button>
                </div>
                {getJogadoresTime(partidaEditando.time1_id).length === 0 && (
                  <p className="text-xs text-yellow-400/70">⚠️ Nenhum jogador cadastrado neste time. Cadastre jogadores na aba "Configurar".</p>
                )}
              </div>
              
              {/* Gols Time 2 */}
              <div className="space-y-3">
                <h4 className="text-purple-300 font-semibold">⚽ Gols de {partidaEditando.time2_nome}</h4>
                <div className="space-y-2">
                  {placar.gols_time2.map((gol, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded">
                      <span className="text-white">{gol.jogador_nome} ({gol.quantidade})</span>
                      <Button variant="ghost" size="sm" onClick={() => removerGol(2, idx)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  {getJogadoresTime(partidaEditando.time2_id).length > 0 ? (
                    <Select 
                      value={golTemp2.jogador_id} 
                      onValueChange={(v) => {
                        const jogador = getJogadoresTime(partidaEditando.time2_id).find(j => j.id === v);
                        setGolTemp2({...golTemp2, jogador_id: v, jogador_nome: jogador?.nome || ''});
                      }}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white flex-1">
                        <SelectValue placeholder="Selecione o jogador..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getJogadoresTime(partidaEditando.time2_id).map(j => (
                          <SelectItem key={j.id} value={j.id}>{j.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input placeholder="Nome do jogador (sem elenco cadastrado)" value={golTemp2.jogador_nome} onChange={(e) => setGolTemp2({...golTemp2, jogador_id: `manual-${Date.now()}`, jogador_nome: e.target.value})} className="bg-white/10 border-white/20 text-white flex-1" />
                  )}
                  <Input type="number" min="1" value={golTemp2.quantidade} onChange={(e) => setGolTemp2({...golTemp2, quantidade: parseInt(e.target.value) || 1})} className="bg-white/10 border-white/20 text-white w-16" />
                  <Button onClick={() => adicionarGol(2)} className="bg-green-600"><Plus className="w-4 h-4" /></Button>
                </div>
                {getJogadoresTime(partidaEditando.time2_id).length === 0 && (
                  <p className="text-xs text-yellow-400/70">⚠️ Nenhum jogador cadastrado neste time. Cadastre jogadores na aba "Configurar".</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartidaModal(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleSalvarPlacar} disabled={salvando} className="bg-green-600">
              {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Salvar Placar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Impressão */}
      <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Printer className="w-5 h-5 text-purple-400" />Imprimir Torneio
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-2 border-b border-white/10 pb-3">
            <Button variant={printType === 'grupos' ? 'default' : 'outline'} size="sm" onClick={() => setPrintType('grupos')} className="border-white/20">Grupos</Button>
            <Button variant={printType === 'partidas' ? 'default' : 'outline'} size="sm" onClick={() => setPrintType('partidas')} className="border-white/20">Partidas</Button>
            <Button variant={printType === 'matamata' ? 'default' : 'outline'} size="sm" onClick={() => setPrintType('matamata')} className="border-white/20">Mata-Mata</Button>
            <Button variant={printType === 'artilheiros' ? 'default' : 'outline'} size="sm" onClick={() => setPrintType('artilheiros')} className="border-white/20">Artilheiros</Button>
          </div>
          
          <div className="flex-1 overflow-y-auto bg-white rounded-lg">
            <div ref={printRef} className="p-6 text-black">
              <div className="print-header text-center border-b-2 border-purple-600 pb-4 mb-4">
                <h1 className="text-xl font-bold text-purple-600">🏆 {torneioSelecionado?.nome}</h1>
                <h2 className="text-base">{getModalidade(torneioSelecionado?.modalidade).nome} - {getGenero(torneioSelecionado?.genero).nome}</h2>
              </div>
              
              {printType === 'grupos' && torneioSelecionado?.grupos?.map(grupo => {
                const classificacao = calcularClassificacaoGrupo(torneioSelecionado, grupo.letra);
                return (
                  <div key={grupo.letra} className="mb-6">
                    <h3 className="section-title text-purple-600 font-bold mb-2">Grupo {grupo.letra}</h3>
                    <table className="print-table w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-purple-600 text-white">
                          <th className="border p-2">#</th>
                          <th className="border p-2 text-left">Time</th>
                          <th className="border p-2">P</th>
                          <th className="border p-2">J</th>
                          <th className="border p-2">V</th>
                          <th className="border p-2">E</th>
                          <th className="border p-2">D</th>
                          <th className="border p-2">GP</th>
                          <th className="border p-2">GC</th>
                          <th className="border p-2">SG</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classificacao.map((time, idx) => (
                          <tr key={time.id} className={idx < 4 ? 'bg-green-50' : ''}>
                            <td className="border p-2 text-center font-bold">{idx + 1}º</td>
                            <td className="border p-2">{time.nome}</td>
                            <td className="border p-2 text-center font-bold">{time.pontos}</td>
                            <td className="border p-2 text-center">{time.jogos}</td>
                            <td className="border p-2 text-center">{time.vitorias}</td>
                            <td className="border p-2 text-center">{time.empates}</td>
                            <td className="border p-2 text-center">{time.derrotas}</td>
                            <td className="border p-2 text-center">{time.gols_pro}</td>
                            <td className="border p-2 text-center">{time.gols_contra}</td>
                            <td className="border p-2 text-center">{time.saldo > 0 ? '+' : ''}{time.saldo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
              
              {printType === 'partidas' && (
                <div>
                  <h3 className="section-title text-purple-600 font-bold mb-2">Cronograma de Partidas</h3>
                  <table className="print-table w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-purple-600 text-white">
                        <th className="border p-2">#</th>
                        <th className="border p-2">Data</th>
                        <th className="border p-2">Horário</th>
                        <th className="border p-2">Grupo</th>
                        <th className="border p-2">Time Casa</th>
                        <th className="border p-2">Placar</th>
                        <th className="border p-2">Time Visitante</th>
                        <th className="border p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...(torneioSelecionado?.partidas_grupos || [])]
                        .sort((a, b) => {
                          // Ordenar por ordem definida pela IA, ou por data/horário
                          if (a.ordem && b.ordem) return a.ordem - b.ordem;
                          const dataA = `${a.data || '9999-99-99'}T${a.horario || '99:99'}`;
                          const dataB = `${b.data || '9999-99-99'}T${b.horario || '99:99'}`;
                          return dataA.localeCompare(dataB);
                        })
                        .map((p, idx) => (
                          <tr key={p.id}>
                            <td className="border p-2 text-center font-bold text-purple-600">{p.ordem || idx + 1}</td>
                            <td className="border p-2 text-center">{p.data || '-'}</td>
                            <td className="border p-2 text-center font-semibold">{p.horario || '-'}</td>
                            <td className="border p-2 text-center">{p.grupo}</td>
                            <td className="border p-2">{p.time1_nome}</td>
                            <td className="border p-2 text-center font-bold">{p.placar1} x {p.placar2}</td>
                            <td className="border p-2">{p.time2_nome}</td>
                            <td className="border p-2 text-center">{p.status === 'finalizada' ? '✓' : '○'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {printType === 'matamata' && torneioSelecionado?.partidas_matamata?.length > 0 && (
                <div>
                  <h3 className="section-title text-red-600 font-bold mb-2">🏆 Fase Eliminatória (Mata-Mata)</h3>
                  <table className="print-table w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-red-600 text-white">
                        <th className="border p-2">Fase</th>
                        <th className="border p-2">Time Casa</th>
                        <th className="border p-2">Placar</th>
                        <th className="border p-2">Time Visitante</th>
                        <th className="border p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {torneioSelecionado?.partidas_matamata
                        ?.sort((a, b) => {
                          const ordem = { quartas: 1, semi: 2, final: 3 };
                          return (ordem[a.fase] || 0) - (ordem[b.fase] || 0);
                        })
                        .map(p => {
                          const faseNome = { quartas: 'Quartas', semi: 'Semifinal', final: '🏆 FINAL' }[p.fase] || p.fase;
                          return (
                            <tr key={p.id} className={p.fase === 'final' ? 'bg-yellow-50 font-semibold' : ''}>
                              <td className="border p-2 text-center">{faseNome}</td>
                              <td className="border p-2">{p.time1_nome}</td>
                              <td className="border p-2 text-center font-bold">{p.placar1} x {p.placar2}</td>
                              <td className="border p-2">{p.time2_nome}</td>
                              <td className="border p-2 text-center">{p.status === 'finalizada' ? '✓' : p.status === 'aguardando' ? '⏳' : '○'}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {printType === 'artilheiros' && (
                <div>
                  <h3 className="section-title text-purple-600 font-bold mb-2">Artilheiros</h3>
                  <table className="print-table w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-purple-600 text-white">
                        <th className="border p-2">#</th>
                        <th className="border p-2 text-left">Jogador</th>
                        <th className="border p-2 text-left">Time</th>
                        <th className="border p-2">Gols</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calcularArtilheiros(torneioSelecionado).map((art, idx) => (
                        <tr key={idx} className={idx === 0 ? 'bg-yellow-50' : ''}>
                          <td className="border p-2 text-center font-bold">{idx + 1}º</td>
                          <td className="border p-2">{art.nome}</td>
                          <td className="border p-2">{art.time_nome}</td>
                          <td className="border p-2 text-center font-bold text-lg">{art.gols}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div className="print-footer text-center text-gray-500 text-xs mt-6 pt-4 border-t">
                Impresso em {new Date().toLocaleDateString('pt-BR')} | www.novodna.com.br | #muitasmentesumamissão
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t border-white/10 pt-4 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowPrintModal(false)} className="border-white/20 text-white">Fechar</Button>
            <Button variant="outline" onClick={() => setShowEmailModal(true)} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
              <Mail className="w-4 h-4 mr-2" />Enviar E-mail
            </Button>
            <Button onClick={() => handleBaixarPDF(printType)} disabled={baixandoPDF} className="bg-green-600">
              {baixandoPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Salvar PDF
            </Button>
            <Button onClick={handlePrint} className="bg-purple-600">
              <Printer className="w-4 h-4 mr-2" />Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Envio por E-mail */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />Enviar por E-mail
            </DialogTitle>
            <DialogDescription className="text-purple-300">
              Envie as tabelas do torneio por e-mail
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-purple-300">Destinatários (separados por vírgula)</Label>
              <Input 
                placeholder="email1@exemplo.com, email2@exemplo.com" 
                value={emailDestinatarios} 
                onChange={(e) => setEmailDestinatarios(e.target.value)} 
                className="bg-white/10 border-white/20 text-white" 
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-purple-300">Assunto (opcional)</Label>
              <Input 
                placeholder={`🏆 ${torneioSelecionado?.nome || 'Torneio'} - Tabelas e Resultados`}
                value={emailAssunto} 
                onChange={(e) => setEmailAssunto(e.target.value)} 
                className="bg-white/10 border-white/20 text-white" 
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-purple-300">Mensagem (opcional)</Label>
              <textarea
                placeholder="Digite uma mensagem personalizada..."
                value={emailMensagem}
                onChange={(e) => setEmailMensagem(e.target.value)}
                className="w-full h-24 px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-purple-400 resize-none"
              />
            </div>
            
            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <p className="text-sm text-blue-300">
                <strong>Conteúdo:</strong> {
                  { grupos: 'Classificação dos Grupos', partidas: 'Partidas da Fase de Grupos', matamata: 'Fase Eliminatória', artilheiros: 'Tabela de Artilheiros' }[printType]
                }
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleEnviarEmail} disabled={enviandoEmail} className="bg-blue-600">
              {enviandoEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Nome do Torneio */}
      <Dialog open={showEditarNomeModal} onOpenChange={setShowEditarNomeModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-purple-400" />Editar Nome do Torneio
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-purple-300">Nome do Torneio</Label>
              <Input 
                placeholder="Ex: Copa Interclasse 2026" 
                value={novoNomeTorneio} 
                onChange={(e) => setNovoNomeTorneio(e.target.value)} 
                className="bg-white/10 border-white/20 text-white" 
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarNomeModal(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleEditarNome} disabled={salvando} className="bg-purple-600">
              {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Gerar Cronograma com IA */}
      <Dialog open={showCronogramaIAModal} onOpenChange={setShowCronogramaIAModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />Gerar Horários com IA
            </DialogTitle>
            <DialogDescription className="text-purple-300">
              A IA irá organizar as partidas para que nenhum time jogue duas vezes seguidas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-purple-300">Data de Início</Label>
                <Input 
                  type="date" 
                  value={cronogramaIA.data_inicio} 
                  onChange={(e) => setCronogramaIA({...cronogramaIA, data_inicio: e.target.value})} 
                  className="bg-white/10 border-white/20 text-white" 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-purple-300">Horário Inicial</Label>
                <Input 
                  type="time" 
                  value={cronogramaIA.horario_inicio} 
                  onChange={(e) => setCronogramaIA({...cronogramaIA, horario_inicio: e.target.value})} 
                  className="bg-white/10 border-white/20 text-white" 
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-purple-300">Intervalo entre jogos (min)</Label>
                <Input 
                  type="number" 
                  min="10" 
                  max="120" 
                  value={cronogramaIA.intervalo_minutos} 
                  onChange={(e) => setCronogramaIA({...cronogramaIA, intervalo_minutos: parseInt(e.target.value) || 30})} 
                  className="bg-white/10 border-white/20 text-white" 
                />
              </div>
              <div className="space-y-2">
                <Label className="text-purple-300">Jogos por dia</Label>
                <Input 
                  type="number" 
                  min="1" 
                  max="50" 
                  value={cronogramaIA.jogos_por_dia} 
                  onChange={(e) => setCronogramaIA({...cronogramaIA, jogos_por_dia: parseInt(e.target.value) || 10})} 
                  className="bg-white/10 border-white/20 text-white" 
                />
              </div>
            </div>
            
            <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
              <p className="text-sm text-purple-300">
                <Sparkles className="w-4 h-4 inline mr-2" />
                A IA vai organizar <strong>{torneioSelecionado?.partidas_grupos?.length || 0} partidas</strong> de forma inteligente, garantindo que nenhum time jogue duas vezes seguidas.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCronogramaIAModal(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={handleGerarCronogramaIA} disabled={gerandoCronograma} className="bg-purple-600">
              {gerandoCronograma ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {gerandoCronograma ? 'Gerando...' : 'Gerar com IA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Configuração Manual de Grupos */}
      <Dialog open={showGruposManualModal} onOpenChange={setShowGruposManualModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-400" />Configurar Grupos Manualmente
            </DialogTitle>
            <DialogDescription className="text-purple-300">
              Arraste os times para os grupos desejados. Ideal para números ímpares de times.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Times não distribuídos */}
            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-yellow-400" />
                Times Disponíveis ({torneioSelecionado?.times?.filter(t => !gruposManual.some(g => g.times.includes(t.id))).length || 0})
              </h4>
              <div className="flex flex-wrap gap-2">
                {torneioSelecionado?.times?.filter(t => !gruposManual.some(g => g.times.includes(t.id))).map(time => (
                  <div key={time.id} className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                    <span className="text-white text-sm">{time.nome}</span>
                    <div className="flex gap-1">
                      {gruposManual.map(g => (
                        <Button
                          key={g.letra}
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-xs bg-purple-500/20 text-purple-300 hover:bg-purple-500/40"
                          onClick={() => adicionarTimeAoGrupo(g.letra, time.id)}
                        >
                          {g.letra}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
                {torneioSelecionado?.times?.filter(t => !gruposManual.some(g => g.times.includes(t.id))).length === 0 && (
                  <p className="text-green-400 text-sm">✓ Todos os times foram distribuídos!</p>
                )}
              </div>
            </div>
            
            {/* Grupos */}
            <div className="grid grid-cols-2 gap-4">
              {gruposManual.map(grupo => (
                <div key={grupo.letra} className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-bold flex items-center gap-2">
                      <Badge className="bg-purple-600">{grupo.letra}</Badge>
                      Grupo {grupo.letra} ({grupo.times.length} times)
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:bg-red-500/20"
                      onClick={() => removerGrupoManual(grupo.letra)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {grupo.times.map(timeId => {
                      const time = torneioSelecionado?.times?.find(t => t.id === timeId);
                      const outrosGrupos = gruposManual.filter(g => g.letra !== grupo.letra);
                      return (
                        <div key={timeId} className="flex items-center justify-between p-2 bg-purple-500/20 rounded group">
                          <span className="text-white text-sm">{time?.nome || 'Time'}</span>
                          <div className="flex items-center gap-1">
                            {/* Botões para mover para outros grupos */}
                            {outrosGrupos.map(outroGrupo => (
                              <Button
                                key={outroGrupo.letra}
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 py-0 text-xs bg-blue-500/20 text-blue-300 hover:bg-blue-500/40"
                                title={`Mover para Grupo ${outroGrupo.letra}`}
                                onClick={() => {
                                  // Mover time para outro grupo
                                  setGruposManual(prev => prev.map(g => {
                                    if (g.letra === grupo.letra) {
                                      return { ...g, times: g.times.filter(t => t !== timeId) };
                                    }
                                    if (g.letra === outroGrupo.letra) {
                                      return { ...g, times: [...g.times, timeId] };
                                    }
                                    return g;
                                  }));
                                }}
                              >
                                →{outroGrupo.letra}
                              </Button>
                            ))}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/20"
                              onClick={() => removerTimeDoGrupo(grupo.letra, timeId)}
                            >
                              ×
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {grupo.times.length === 0 && (
                      <p className="text-center text-purple-300/50 text-sm py-4">Grupo vazio</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              variant="outline"
              onClick={adicionarGrupoManual}
              className="border-dashed border-purple-500/50 text-purple-300"
            >
              <Plus className="w-4 h-4 mr-2" />Adicionar Grupo
            </Button>
            
            {/* Resumo */}
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <h4 className="text-blue-400 font-semibold mb-2">📊 Resumo</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-purple-300">Total de times:</span>
                  <span className="text-white ml-2 font-bold">{torneioSelecionado?.times?.length || 0}</span>
                </div>
                <div>
                  <span className="text-purple-300">Grupos:</span>
                  <span className="text-white ml-2 font-bold">{gruposManual.length}</span>
                </div>
                <div>
                  <span className="text-purple-300">Distribuídos:</span>
                  <span className="text-white ml-2 font-bold">{gruposManual.reduce((acc, g) => acc + g.times.length, 0)}</span>
                </div>
              </div>
              <div className="mt-2 text-xs text-purple-300">
                Partidas estimadas: ~{gruposManual.reduce((acc, g) => acc + (g.times.length * (g.times.length - 1) / 2), 0)} (todos contra todos por grupo)
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t border-white/10 pt-4">
            <Button variant="outline" onClick={() => setShowGruposManualModal(false)} className="border-white/20 text-white">Cancelar</Button>
            <Button onClick={salvarGruposManual} className="bg-green-600">
              <Save className="w-4 h-4 mr-2" />Salvar e Gerar Partidas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TorneioProfissional;
